/**
 * WorkCard component for displaying a single work in the bulk download UI.
 * Shows thumbnail, title, metadata (author, year, views), and optional relevancy badge.
 */

import React from 'react';
import styled from 'astroturf/react';
import Badge from 'js/design_system/Badge';
import { Body } from 'js/design_system/Body';
import $image_url from 'js/utils/image_url';

import { BundleWork } from './types';

function formatRelevancyBadgeText(
  relevancy: number,
  label: 'match' | 'relevancy' = 'relevancy',
): string {
  return `${relevancy}% ${label}`;
}

/**
 * Normalize ALL-CAPS author names to title case for display.
 *
 * Only transforms the string when it is **entirely** uppercase
 * (e.g. "MARIA ELVIRA RUIZ" -> "Maria Elvira Ruiz").
 * Mixed-case names like "McDonald", "de la Cruz", or "van der Waals"
 * are returned unchanged to avoid mangling them.
 *
 * Uses locale-aware case conversion so accented characters are handled
 * correctly (e.g. "JOSÉ MARÍA" -> "José María").
 */
function normalizeAllCapsName(name: string): string {
  // Only transform when the entire string is uppercase; leave
  // properly-cased names untouched.
  if (name !== name.toLocaleUpperCase()) return name;

  return name
    .toLocaleLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
}

/** Build metadata parts for work card: authors, year, views */
function getWorkMetaParts(work: BundleWork): string[] {
  const parts: string[] = [];
  if (work.authors) {
    parts.push(`by ${normalizeAllCapsName(work.authors)}`);
  }
  if (work.year) {
    parts.push(String(work.year));
  }
  if (work.viewCount) {
    parts.push(`${work.viewCount} ${work.viewCount === 1 ? 'view' : 'views'}`);
  }
  return parts;
}

/** Render work metadata with separators and proper spacing */
function WorkMetaLine({ work }: { work: BundleWork }) {
  const parts = getWorkMetaParts(work);
  if (parts.length === 0) return null;

  return (
    <WorkSubRow>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Body size="xs" as="span" style={secondaryText}>
              |
            </Body>
          )}
          <Body size="xs" as="span" style={secondaryText}>
            {part}
          </Body>
        </React.Fragment>
      ))}
    </WorkSubRow>
  );
}

export interface WorkCardProps {
  work: BundleWork;
  /** Optional header text displayed above the work (e.g., "The paper you are reading") */
  header?: string;
  /** Whether to show the relevancy badge */
  showRelevancy?: boolean;
  /** Badge wording: "relevancy" (default) or "match". */
  relevancyLabel?: 'match' | 'relevancy';
  /** Whether to show admin-only debug annotations (bucket, similarity score). */
  showAnnotations?: boolean;
  /** Compact padding: reduced vertical padding, zero horizontal (parent provides it). */
  compact?: boolean;
  /** Where to render the relevancy badge. Default is 'side' (right of title).
   *  'above' places it above the title inside the meta column. */
  badgePlacement?: 'side' | 'below' | 'above';
}

export function WorkCard({
  work,
  header,
  showRelevancy = true,
  relevancyLabel = 'relevancy',
  showAnnotations = false,
  compact = false,
  badgePlacement = 'side',
}: WorkCardProps) {
  const thumbnailSrc = work.thumbnailUrl
    ? $image_url(work.thumbnailUrl)
    : '/images/blank-paper.jpg';

  const hasAnnotationData = work.bucket || work.similarityScore != null;
  const shouldShowAnnotations = hasAnnotationData && showAnnotations;

  const badgeStyles = {
    border: '1px solid var(--brand-colors-expanded-colors-lime-200, #dee6d6)',
  };
  const badgeText = formatRelevancyBadgeText(
    work.relevancy ?? 0,
    relevancyLabel,
  );

  const badge =
    showRelevancy &&
    work.relevancy != null &&
    (compact || badgePlacement === 'above' ? (
      <CompactBadgeWrapper>
        <Badge
          text={badgeText}
          backgroundColor="var(--brand-colors-expanded-colors-lime-100, #f2f5ef)"
          color="var(--brand-colors-expanded-colors-lime-900, #333e2c)"
          height="22px"
          style={{
            ...badgeStyles,
            fontSize: badgePlacement === 'above' ? '12px' : '11px',
            borderRadius: '6px',
            padding: '0 6px',
          }}
        />
      </CompactBadgeWrapper>
    ) : (
      <RelevancyBadge
        text={badgeText}
        backgroundColor="var(--brand-colors-expanded-colors-lime-100, #f2f5ef)"
        color="var(--brand-colors-expanded-colors-lime-900, #333e2c)"
        style={badgeStyles}
      />
    ));

  return (
    <WorkCardContainer data-compact={compact || undefined}>
      {header && (
        <Body size="xs" as="div" style={secondaryText}>
          {header}
        </Body>
      )}
      <WorkRow data-compact={compact || undefined}>
        <Thumb>
          <img alt="" src={thumbnailSrc} />
        </Thumb>
        <WorkMeta>
          {badgePlacement === 'above' && badge}
          <TitleClamp>
            <Body size="sm" bold as="span">
              {work.title}
            </Body>
          </TitleClamp>
          <WorkMetaLine work={work} />
          {badgePlacement === 'below' && badge}
        </WorkMeta>
        {badgePlacement === 'side' && badge}
      </WorkRow>
      {shouldShowAnnotations && (
        <AnnotationRow>
          {work.bucket && (
            <AnnotationTag>
              {work.bucket}
              {work.bucketIndex != null && `[${work.bucketIndex}]`}
            </AnnotationTag>
          )}
          {work.similarityScore != null && (
            <AnnotationTag>
              sim: {work.similarityScore.toFixed(3)}
            </AnnotationTag>
          )}
        </AnnotationRow>
      )}
    </WorkCardContainer>
  );
}

const secondaryText: React.CSSProperties = {
  color: 'var(--text-secondary, #535366)',
};

// Styled components

const WorkCardContainer = styled.div`
  padding: var(--card-v-padding, var(--spacing-xs-16, 16px))
    var(--card-h-padding, var(--spacing-xs-16, 16px));
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);

  &[data-compact] {
    padding: 10px 0;
    gap: var(--spacing-xs-8, 8px);
  }

  &:not(:first-child) {
    border-top: 1px solid var(--brand-colors-neutral-400, #dddde2);
  }
`;

const WorkRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-12, 12px);
  align-items: flex-start;

  &[data-compact] {
    align-items: center;
  }
`;

const Thumb = styled.div`
  width: 50px;
  height: 72px;
  border: 1px solid var(--stroke-beige-light, #ccc9bc);
  background: var(--background-white, #ffffff);
  overflow: hidden;
  flex: 0 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const WorkMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

/** Clamps title text to 2 lines. Typography is handled by the Body inside. */
const TitleClamp = styled.div`
  color: var(--text-primary, #141413);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const WorkSubRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
`;

const RelevancyBadge = styled(Badge)`
  flex-shrink: 0;
  height: 28px;
  border-radius: var(--border-radius-radius-md);
`;

const CompactBadgeWrapper = styled.span`
  align-self: flex-start;
  flex-shrink: 0;
  display: inline-flex;
`;

const AnnotationRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-8, 8px);
  flex-wrap: wrap;
`;

/** Debug annotation pill. Uses monospace directly (not DS Mono which
 *  applies text-transform: uppercase, mangling camelCase bucket names). */
const AnnotationTag = styled.span`
  font-family: monospace;
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 18px);
  color: var(--text-secondary, #535366);
  background: var(--background-light-blue, #eef2f9);
  padding: 2px 6px;
  border-radius: var(--border-radius-radius-sm);
`;

export default WorkCard;
